<template>
	<form class="cards-form" enctype="multipart/form-data" @submit.prevent="submitForm">
		<input type="hidden" name="_method" value="PUT">
		<div class="form-step">
			<div class="form-headline">
				<h3 class="form-title">{{ $t('cards.card_detail') }}</h3>
			</div>
			<div class="card-preview">
				<figure class="image fit-image" v-if="data.card_category.code !== 'custom'">
					<img :src="cardPreviewImage">
				</figure>
				<div class="custom-card-preview" v-else :style="`background: ${color};`">
					<div class="icon">
						<i class="fa-solid fa-credit-card-blank"></i>
					</div>
					<div class="custom-card-preview-title">{{ name ? name : $t('forms.card_title') }}</div>
				</div>
			</div>
			<div class="barcode-container" v-show="!isEditing" v-if="data.number">
				<svg ref="barcode" v-if="!data.card_category.qr"></svg>
				<canvas ref="qrCode" v-else></canvas>
			</div>
			<errors-list :errors="errors"/>
			<div class="form-row" v-if="data.card_category.code === 'custom'">
				<field-box name="name" :label="$t('forms.card_title')" type="text" :value="data.name"
				           :disabled="!isEditing"/>
			</div>
			<div class="form-row">
				<field-box name="number" :label="$t('forms.card_number')" type="text" :value="data.number"
				           :disabled="!isEditing"/>
			</div>
			<div class="form-row" v-if="data.card_category.code === 'custom' && isEditing">
				<color-picker name="color" :colors="colors" :selected="color" :label="$t('forms.choose_color')"/>
			</div>
			<div class="attachments-container" v-if="data.__offline && (data.front_local_uri || data.back_local_uri)">
				<figure class="attachment-image fit-image" v-if="data.front_local_uri" @click="openImage(data.front_local_path)">
					<img :src="data.front_local_uri">
				</figure>
				<figure class="attachment-image fit-image" v-if="data.back_local_uri" @click="openImage(data.back_local_path)">
					<img :src="data.back_local_uri">
				</figure>
			</div>
			<div class="attachments-container" v-else-if="!data.__offline && (data.front_side || data.back_side)">
				<attachment-preview-field :data="data.front_side" v-if="data.front_side" :allow-remove="isEditing"
				                          name="remove_front_side"/>
				<attachment-preview-field :data="data.back_side" v-if="data.back_side" :allow-remove="isEditing"
				                          name="remove_back_side"/>
			</div>
			<div class="dropzone-boxes-container" v-if="isEditing">
				<dropzone-box name="front_side_id"
				              :label="$t('forms.upload_front_side')"
				              url="api/cards/attachment"
				              :max-files="1"/>
				<dropzone-box name="back_side_id"
				              :label="$t('forms.upload_back_side')"
				              url="api/cards/attachment"
				              :max-files="1"/>
			</div>
		</div>
		<div class="form-actions" v-if="!data.__offline">
			<button class="button style-4" type="button" v-if="!isEditing" @click.prevent="isEditing = true">
				<span class="inner">{{ $t('forms.edit') }}</span>
			</button>
			<button class="button style-4" type="submit" :class="loading ? 'loading' : ''" v-else>
				<span class="inner">{{ $t('forms.save') }}</span>
			</button>
			<button class="button style-1" type="button"
			        @click="$store.commit('HIDE_CARDS_EDIT_FORM')">
				<span class="inner">{{ $t('forms.cancel') }}</span>
			</button>
		</div>
	</form>
</template>

<script>
	import pageFunctionsMixin from "../../mixins/pageFunctionsMixin";
	import { trackEvent } from '@/analytics';
	import ErrorsList from "../containers/ErrorsList";
	import EditableValueField from "../form-fields/EditableValueField";
	import FieldBox from "../form-fields/FieldBox";
	import ColorPicker from "../form-fields/ColorPicker";
	import DropzoneBox from "../form-fields/DropzoneBox";
	import { Filesystem, Directory } from '@capacitor/filesystem';
	import { FileOpener } from '@capacitor-community/file-opener';
	import CheckboxSwitcher from "../form-fields/CheckboxSwitcher";
	import AttachmentPreviewField from "../form-fields/AttachmentPreviewField";
	import JsBarcode from 'jsbarcode';
	import QRCode from 'qrcode';

	export default {
		name: "CardsEditForm",
		props: ['data'],
		mixins: [pageFunctionsMixin],
		components: {
			ErrorsList,
			EditableValueField,
			FieldBox,
			DropzoneBox,
			CheckboxSwitcher,
			ColorPicker,
			AttachmentPreviewField
		},
		data() {
			return {
				isEditing: false,
				category: false,
				loading: false,
				errors: false,
				name: this.data.name,
				color: this.data.color,
				colors: [
					'#4951CB',
					'#4981CB',
					'#49B9CB',
					'#49CB93',
					'#7DCB49',
					'#C7CB49',
					'#CB9349',
				],
			}
		},
		computed: {
			cardPreviewImage() {
				return this.getImage(this.data.card_category.logo.filename);
			},

		},
		methods: {
			chooseCategory(id) {
				this.category = id;
			},

			async openImage(path) {
				if (!path) return;
				try {
					const { uri } = await Filesystem.getUri({ path, directory: Directory.Data });
					await FileOpener.open({ filePath: uri, openWithDefault: true });
				} catch (e) {
					console.error('openImage failed', e);
				}
			},

			submitForm(e) {
				let form = e.target;
				let formData = new FormData(form);

				this.loading = true;
				trackEvent('form_submit', { form_name: 'card_edit', module: 'cards' });
				this.axios.post(`/api/cards/${this.data.id}`, formData).then(response => {
					this._submitSuccess = true;
					trackEvent('form_submit_success', { form_name: 'card_edit', module: 'cards' });
					this.resetForm(form);
					this.loading = false;

					this.$store.dispatch('updateCard', response.data);
					if (this.$store.state.app.native) {
						this.$store.dispatch('syncCardsOffline');
					}
					this.$store.commit('HIDE_CARDS_EDIT_FORM');
				}).catch(error => {
					trackEvent('form_submit_error', {
						form_name: 'card_edit',
						module: 'cards',
						error_message: error.response && error.response.data ? error.response.data.message : error.message,
						error_status: error.response ? String(error.response.status) : null,
					});
					this.$swal.fire({
						icon: 'error',
						title: 'Oops',
						text: error.response.data.message
					});

					if (error.response.data.errors) {
						this.errors = error.response.data.errors;
					}

					this.loading = false;
				});
			},
			initBarcode() {
				JsBarcode(this.$refs.barcode, this.data.number);
			},
			initQrCode(){
				QRCode.toCanvas(this.$refs.qrCode, this.data.number, {
					width: 225
				});
			},
			initPreviews(){
				if (!this.data.card_category.qr){
					this.initBarcode();
				} else {
					this.initQrCode();
				}
			},
			resetForm(form) {
				form.reset();

				let frontSideInput = form.querySelector('input[name="front_side_id"]');
				if (frontSideInput) {
					frontSideInput.parentNode.removeChild(frontSideInput);
				}

				let backSideInput = form.querySelector('input[name="back_side_id"]');
				if (backSideInput) {
					backSideInput.parentNode.removeChild(backSideInput);
				}

				let dzPreviews = form.querySelectorAll('.dz-preview');
				if (dzPreviews.length) {
					for (let dzPreview of dzPreviews) {
						dzPreview.parentNode.removeChild(dzPreview);
					}
				}

				let dropzoneEls = form.querySelectorAll('.dropzone');
				if (dropzoneEls.length) {
					for (let dropzoneEl of dropzoneEls) {
						dropzoneEl.classList.remove('dz-started');
					}
				}
			}
		},
		mounted() {
			this._submitSuccess = false;
			trackEvent('form_open', { form_name: 'card_edit', module: 'cards' });
			if (this.data.number) {
				this.initPreviews();
			}

			this.$root.$on('field-changed', (data) => {
				switch (data.name) {
					case 'name':
						this.name = data.value;
						break;
					case 'color':
						this.color = data.value;
						break;
				}
			});
		},
		beforeDestroy() {
			if (!this._submitSuccess) {
				trackEvent('form_close', { form_name: 'card_edit', module: 'cards' });
			}
		},
	}
</script>

<style scoped lang="scss">

	.button {
		width: 100%;
	}

	.attachments-container {
		.attachment-image {
			position: relative;
			width: calc(50% - 8px);
			&:before { padding-bottom: 138%; }
			img { border-radius: 3px; }
		}
	}

	.card-preview {
		margin-bottom: 30px;

		&:last-child {
			margin-bottom: 0;
		}

		.custom-card-preview {
			width: 230px;
			height: 170px;
			border-radius: 5px;
			margin: 0 auto;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-direction: column;
			color: var(--white-color, $whiteColor);
			overflow: hidden;

			.custom-card-preview-title {
				max-width: 90%;
				margin: 0 auto;
				text-align: center;
			}
		}


		.image {
			width: 230px;
			height: 170px;
			border-radius: 5px;
			margin: 0 auto;
			overflow: hidden;

		}
	}

	.form-step {
		margin-bottom: 35px;
		&:last-child {
			margin-bottom: 0;
		}
	}

	.form-headline {
		margin-bottom: 35px;

		&:last-child {
			margin-bottom: 0;
		}
	}

	.form-title {
		font-size: 26px;
		text-align: center;
	}

	.card-categories-container {
		margin-bottom: 50px;

		&:last-child {
			margin-bottom: 0;
		}
	}

	.barcode-container {
		margin-bottom: 30px;
		text-align: center;

		svg {
			max-width: 100%;
		}
		canvas {
			margin: 0 auto;
			vertical-align: middle;
		}
	}

	.card-categories-container-title {
		font-size: 26px;
		text-align: center;
		padding-bottom: 15px;
		//border-bottom: 1px solid transparentize($whiteColor, .85);
		border-bottom: rgba(var(--white-color-rgb), .15);
	}

	.card-category-box {
		margin-bottom: 5px;

		&:last-child {
			margin-bottom: 0;
		}

		a {
			display: flex;
			align-items: center;

			&:hover {
				.card-category-title {
					color: var(--primary-color, $primaryColor);
				}
			}
		}

		.icon,
		.image {
			width: 80px;
			height: 60px;
			border-radius: 5px;
			margin-right: 20px;
			overflow: hidden;
		}

		.icon {
			background: var(--white-color, $whiteColor);
			margin-bottom: 0;
			font-size: 24px;
			color: var(--primary-color, $primaryColor);
			display: flex;
			align-items: center;
			justify-content: center;
		}


		.card-category-title {
			font-size: 18px;
			font-weight: 600;
			color: var(--white-color, $whiteColor);
			transition: $animationDefaultDuration;
		}
	}


	.cards-form {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
	}


	.icon {
		text-align: center;
		font-size: 21px;
		margin-bottom: 10px;

		&:last-child {
			margin-bottom: 0;
		}
	}

	.form-row {
		margin-bottom: 15px;

		&:last-child {
			margin-bottom: 0;
		}

		&.two-columns {
			display: flex;
			justify-content: space-between;
			flex-direction: row;

			.field-box {
				width: calc(50% - 8px);
			}
		}
	}

	.form-actions {
		margin-top: auto;
		display: flex;
		flex-direction: column;

		.button {
			margin-bottom: 10px;

			&:last-child {
				margin-bottom: 0;
			}
		}
	}

	.dropzone-boxes-container {
		display: flex;
		justify-content: space-between;

		.dropzone-box {
			width: calc(50% - 8px);
		}
	}

	@media($mobileS) {
		.card-categories-container-title,
		.form-title {
			font-size: 21px;
		}
		.form-actions {
			margin-top: 25px;
		}
		.dropzone-boxes-container {
			flex-direction: column;

			.dropzone-box {
				width: 100%;
				margin-bottom: 10px;

				&:last-child {
					margin-bottom: 0;
				}
			}
		}
		.form-row {
			margin-bottom: 10px;

			&.two-columns {
				flex-direction: column;

				.field-box {
					width: 100%;
					margin-bottom: 10px;

					&:last-child {
						margin-bottom: 0;
					}
				}
			}
		}
	}
</style>